import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, MessageCircle, CalendarCheck, Megaphone, TrendingUp, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import SignalFeed from "@/components/SignalFeed";

const StatCard = ({ icon: Icon, label, value, accent = "text-gold", trend }) => (
  <div className="glass-card glass-card-hover p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="label-eyebrow">{label}</div>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className="font-display text-4xl text-white tracking-tight">{value}</div>
    {trend && <div className="text-xs text-emerald-400 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{trend}</div>}
  </div>
);

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  // one-shot fetch on mount
  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/leads").then(({ data }) => setRecent(data.slice(0, 6))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="label-eyebrow mb-2">Welcome back, {user?.name?.split(" ")[0]}</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Your workspace, at a glance.</h1>
          <p className="text-zinc-400 mt-2">Real leads sourced, drafts ready, pipeline moving — here's what's live right now.</p>
        </div>
        <Link to="/dashboard/icp" className="btn-ghost text-sm" data-testid="header-edit-icp">
          <Sparkles className="w-4 h-4" /> Refine ICP
        </Link>
      </div>

      {stats?.sample_lead_count > 0 && (
        <div className="mb-6 glass-card p-4 border-amber-400/30 bg-amber-400/[0.04] flex items-start gap-3" data-testid="sample-data-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-white mb-1">Your workspace contains sample data</div>
            <div className="text-sm text-zinc-400">
              {stats.sample_lead_count} of your leads (and any default campaigns) are demo placeholders, not real prospects. Click{" "}
              <Link to="/dashboard/leads" className="text-emerald-300 hover:underline">Mine real leads</Link> on the Leads page to source verified nonprofits from ProPublica IRS 990 filings.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard icon={Users} label="Leads found" value={stats?.total_leads ?? "—"} trend={stats ? `+${Math.floor(stats.total_leads * 0.18)} this week` : ""} />
        <StatCard icon={MessageCircle} label="Engaged" value={stats?.engaged ?? "—"} />
        <StatCard icon={Megaphone} label="Active campaigns" value={stats?.active_campaigns ?? "—"} />
        <StatCard icon={CalendarCheck} label="Meetings booked" value={stats?.meetings ?? "—"} accent="text-emerald-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        {/* Signal feed */}
        <div className="lg:col-span-2">
          <SignalFeed />
        </div>

        <div className="glass-card p-6" data-testid="reply-rate-card">
          <div className="label-eyebrow mb-2">Reply rate</div>
          <div className="font-display text-5xl text-white mb-2">{stats?.reply_rate ?? 0}%</div>
          <div className="text-sm text-zinc-400 mb-6">Computed from leads you've moved to "Replied" or "Meeting".</div>
          <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
                 style={{ width: `${Math.min((stats?.reply_rate || 0) * 5, 100)}%` }} />
          </div>
          <div className="mt-6 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
            <div className="text-xs text-emerald-300 font-medium mb-1">How this moves</div>
            <div className="text-sm text-zinc-300 leading-relaxed">
              Every time you flip a lead's status to Replied or Meeting on the Leads page, this ticks up. No inflated benchmarks.
            </div>
          </div>
        </div>
      </div>

      {/* Recent leads */}
      <div className="glass-card p-6" data-testid="recent-leads">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label-eyebrow mb-1">Most recent</div>
            <div className="font-display text-xl text-white">Latest leads in your workspace</div>
          </div>
          <Link to="/dashboard/leads" className="text-sm text-emerald-300 hover:underline" data-testid="view-all-leads">View all →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {recent.map((l) => (
            <div key={l.id} className="glass-card glass-card-hover p-4" data-testid={`recent-lead-${l.id}`}>
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <div className="text-white font-medium text-sm truncate">{l.name}</div>
                    {l.real_source ? (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 flex-shrink-0" title="Real IRS-registered nonprofit from ProPublica"><ShieldCheck className="w-2 h-2" />Real</span>
                    ) : (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-300 border border-amber-400/30 flex items-center gap-1 flex-shrink-0" title="Demo lead — not verified against a real source"><AlertTriangle className="w-2 h-2" />Demo</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{l.title}</div>
                </div>
                <div className="text-xs font-mono text-gold flex-shrink-0">{l.score}</div>
              </div>
              <div className="text-sm text-zinc-300 truncate">{l.company}</div>
              <div className="text-xs text-zinc-500 mt-1">{l.industry} · {l.location}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
